import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { formatCLP, formatFecha, hoyISO, haceDiasISO, agruparPorMes, claveMes, nombreMes } from '../lib/format';
import { q, crearMovimiento, borrarMovimientoSimple } from '../lib/vinculos';
import { gananciaVenta, totalVenta, ventaConMonto, stockPorProducto, costoPromedio, metricasProducto, preciosPorProveedor, mejorProveedor } from '../lib/negocio';
import { DOCUMENTOS_VENTA, conIva, ivaDesdeTotal, netoDesdeTotal, ivaDelMes, nombreDocumento } from '../lib/iva';
import { useAccion } from '../lib/useAccion';
import ErrorBanner from './ErrorBanner';

const METODOS = [
  { value: 'efectivo', label: '💵 Efectivo', icono: '💵', titulo: 'En efectivo' },
  { value: 'transferencia', label: '🏦 Transferencia', icono: '🏦', titulo: 'Por transferencia' },
  // Mercado Libre administra la plata aparte: la venta descuenta stock y no toca las categorías
  { value: 'mercadolibre', label: '🛒 Mercado Libre', icono: '🛒', titulo: 'Por Mercado Libre' },
];

const iconoMetodo = (valor) => METODOS.find(m => m.value === valor)?.icono || '💵';

const TABS = [
  { key: 'resumen', label: '📊 Resumen' },
  { key: 'productos', label: '🏷️ Productos' },
  { key: 'ventas', label: '🧾 Ventas' },
  { key: 'reponer', label: '📦 Qué comprar' },
  { key: 'iva', label: '🧾 IVA' },
];

const num = (v) => parseFloat(v) || 0;
const fmtCant = (n) => Number(n || 0).toLocaleString('es-CL', { maximumFractionDigits: 2 });
const fmtPct = (n) => `${Math.round(n)}%`;

const productoVacio = { sku: '', nombre: '', categoria: '', descripcion: '', costo_unitario: '', precio_venta: '', stock_minimo: '', stock_inicial: '', bolsillo_id: '' };
const ventaVacia = () => ({ producto_id: '', cantidad: 1, precio_unitario: '', metodo_pago: 'efectivo', documento: 'boleta', costo_extra: '', fecha: hoyISO(), cliente: '', nota: '', bolsillo_id: '' });
const stockVacio = () => ({
  producto_id: '', tipo: 'entrada', cantidad: '', costo_unitario: '', con_factura: false,
  proveedor: '', fecha: hoyISO(), nota: '', bolsillo_id: '', direccion: 'restar',
  nuevoSku: '', nuevoNombre: '', nuevaCategoria: '', nuevoPrecio: '', nuevoMinimo: '',
});

function Negocio({ userId }) {
  const [productos, setProductos] = useState([]);
  const [movStock, setMovStock] = useState([]);
  const [ventas, setVentas] = useState([]);
  const [bolsillos, setBolsillos] = useState([]);
  const [compras, setCompras] = useState([]); // para el IVA crédito de Mis Compras
  const [egresos, setEgresos] = useState([]); // salidas de dinero marcadas con factura
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('resumen');
  const [aviso, setAviso] = useState('');
  const { saving, error, setError, run } = useAccion();

  const [productoModal, setProductoModal] = useState(null); // {} = nuevo, { id } = editar, null = cerrado
  const [formProducto, setFormProducto] = useState(productoVacio);
  const [ventaModal, setVentaModal] = useState(false);
  const [formVenta, setFormVenta] = useState(ventaVacia);
  const [stockModal, setStockModal] = useState(null); // producto
  const [formStock, setFormStock] = useState(stockVacio);

  const [mesResumen, setMesResumen] = useState(claveMes(hoyISO()));
  const [busqueda, setBusqueda] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [verArchivados, setVerArchivados] = useState(false);
  const [expandido, setExpandido] = useState(null);
  const [mesesAbiertos, setMesesAbiertos] = useState(null); // null = solo el mes más reciente

  const loadData = useCallback(async () => {
    if (!userId) return;
    const [p, ms, v, b, co, eg] = await Promise.all([
      q(supabase.from('productos').select('*').eq('user_id', userId).order('sku')),
      q(supabase.from('movimientos_stock').select('*').eq('user_id', userId).order('fecha', { ascending: false })),
      q(supabase.from('ventas').select('*').eq('user_id', userId).order('fecha', { ascending: false })),
      q(supabase.from('bolsillos').select('*').eq('user_id', userId).order('created_at')),
      q(supabase.from('compras').select('id, fecha, tienda, total, con_factura, iva, movimiento_id').eq('user_id', userId)),
      q(supabase.from('bolsillo_movimientos').select('id, fecha, tipo, monto, descripcion, rubro, con_factura, iva').eq('user_id', userId).eq('tipo', 'egreso')),
    ]);
    setProductos(p || []);
    setCompras(co || []);
    setEgresos(eg || []);
    setMovStock(ms || []);
    setVentas(v || []);
    setBolsillos(b || []);
  }, [userId]);

  useEffect(() => {
    loadData().catch(e => setError('Error cargando datos: ' + e.message)).finally(() => setLoading(false));
  }, [loadData, setError]);

  // --- Cálculos ---
  const stock = stockPorProducto(movStock, ventas);
  const getStock = (id) => stock[id] || 0;
  const productoPorId = (id) => productos.find(p => p.id === id);
  const activos = productos.filter(p => p.activo);
  const ventasDe = (id) => ventas.filter(v => v.producto_id === id);
  const desde30 = haceDiasISO(30);
  const vendidos30 = (id) => ventas
    .filter(v => v.producto_id === id && !v.devuelta && v.fecha >= desde30)
    .reduce((s, v) => s + Number(v.cantidad), 0);
  const nombreBolsillo = (id) => bolsillos.find(b => b.id === id)?.nombre;

  // Efectivo → primera categoría tipo efectivo; transferencia → primera tipo tarjeta/banco.
  // Mercado Libre no entra a ninguna: la plata la retiene Meli y llega después, por su cuenta.
  const bolsilloPorMetodo = (metodo) => (metodo === 'mercadolibre'
    ? ''
    : bolsillos.find(b => b.tipo === (metodo === 'efectivo' ? 'efectivo' : 'tarjeta'))?.id || '');

  const badgeStock = (p) => {
    const s = getStock(p.id);
    const cls = s <= 0 ? 'badge-danger' : s <= p.stock_minimo ? 'badge-warning' : 'badge-success';
    return <span className={`badge ${cls}`}>{fmtCant(s)}</span>;
  };

  // --- Productos ---
  const abrirNuevoProducto = () => { setFormProducto(productoVacio); setProductoModal({}); };

  const abrirEditarProducto = (p) => {
    setFormProducto({
      ...productoVacio,
      sku: p.sku,
      nombre: p.nombre,
      categoria: p.categoria || '',
      descripcion: p.descripcion || '',
      costo_unitario: p.costo_unitario,
      precio_venta: p.precio_venta,
      stock_minimo: p.stock_minimo,
    });
    setProductoModal({ id: p.id });
  };

  // Entrada de mercadería; si se pagó desde una categoría, se registra la salida de dinero vinculada.
  // Entrada de mercadería. `costo` es el costo que queda guardado (neto si hubo factura) y
  // `costoPagado` lo que realmente salió del bolsillo (con IVA incluido).
  const registrarEntrada = async (producto, { cantidad, costo, costoPagado, conFactura = false, fecha, bolsilloId, proveedor, nota }) => {
    const pagado = cantidad * (costoPagado ?? costo);
    const ivaCompra = conFactura ? ivaDesdeTotal(pagado) : 0;
    const mov = bolsilloId && pagado > 0
      ? await crearMovimiento({
          userId,
          bolsilloId,
          tipo: 'egreso',
          monto: pagado,
          descripcion: `Compra mercadería: ${producto.sku} ×${fmtCant(cantidad)}`,
          fecha,
          rubro: 'empresa',
          conFactura,
          iva: ivaCompra,
        })
      : null;
    try {
      await q(supabase.from('movimientos_stock').insert({
        user_id: userId,
        producto_id: producto.id,
        tipo: 'entrada',
        cantidad,
        costo_unitario: costo,
        con_factura: conFactura,
        iva: ivaCompra,
        fecha,
        proveedor: proveedor || null,
        nota: nota || null,
        bolsillo_id: mov ? bolsilloId : null,
        movimiento_id: mov?.id || null,
      }));
    } catch (err) {
      await borrarMovimientoSimple(mov?.id);
      throw err;
    }
  };

  const handleGuardarProducto = async (e) => {
    e.preventDefault();
    const datos = {
      sku: formProducto.sku.trim().toUpperCase(),
      nombre: formProducto.nombre.trim(),
      categoria: formProducto.categoria.trim() || null,
      descripcion: formProducto.descripcion.trim() || null,
      costo_unitario: num(formProducto.costo_unitario),
      precio_venta: num(formProducto.precio_venta),
      stock_minimo: num(formProducto.stock_minimo),
    };
    if (!datos.sku || !datos.nombre) return;
    const editandoId = productoModal.id;
    const ok = await run(async () => {
      try {
        if (editandoId) {
          await q(supabase.from('productos').update(datos).eq('id', editandoId));
          return;
        }
        const nuevo = await q(supabase.from('productos').insert({ user_id: userId, ...datos }).select().single());
        const inicial = num(formProducto.stock_inicial);
        if (inicial > 0) {
          await registrarEntrada(nuevo, {
            cantidad: inicial,
            costo: datos.costo_unitario,
            fecha: hoyISO(),
            bolsilloId: formProducto.bolsillo_id,
            nota: 'Stock inicial',
          });
        }
      } catch (err) {
        if (err.code === '23505') throw new Error(`Ya existe un producto con el SKU ${datos.sku}.`);
        throw err;
      }
    }, loadData);
    if (ok) setProductoModal(null);
  };

  const handleArchivar = (p) =>
    run(() => q(supabase.from('productos').update({ activo: !p.activo }).eq('id', p.id)), loadData);

  const handleEliminarProducto = async (p) => {
    if (!window.confirm(`¿Eliminar ${p.sku} — ${p.nombre}?\n\nTambién se borra su historial de stock. Las salidas de dinero ya registradas en tus categorías se mantienen.`)) return;
    await run(() => q(supabase.from('productos').delete().eq('id', p.id)), loadData);
  };

  // --- Ventas ---
  const abrirVenta = (producto) => {
    const f = ventaVacia();
    f.bolsillo_id = bolsilloPorMetodo(f.metodo_pago);
    if (producto) {
      f.producto_id = producto.id;
      f.precio_unitario = producto.precio_venta || '';
    }
    setFormVenta(f);
    setVentaModal(true);
  };

  const handleGuardarVenta = async (e) => {
    e.preventDefault();
    const producto = productoPorId(formVenta.producto_id);
    const cantidad = num(formVenta.cantidad);
    const precio = num(formVenta.precio_unitario);
    if (!producto || cantidad <= 0) { setError('Elige el producto y la cantidad vendida.'); return; }
    const enStock = getStock(producto.id);
    if (cantidad > enStock && !window.confirm(`Solo tienes ${fmtCant(enStock)} en stock de ${producto.sku}. ¿Registrar la venta igual?`)) return;
    const total = cantidad * precio;
    // Por Meli no se pide documento ni gastos extra: esa venta ya va al SII por la plataforma
    const documento = ventaEsMeli ? 'ninguno' : formVenta.documento;
    const costoExtra = ventaEsMeli ? 0 : num(formVenta.costo_extra);
    // Con boleta o factura, parte de lo que cobras es IVA que después hay que declarar
    const ivaVenta = conIva(documento) ? ivaDesdeTotal(total) : 0;
    const ok = await run(async () => {
      const mov = formVenta.bolsillo_id && total > 0
        ? await crearMovimiento({
            userId,
            bolsilloId: formVenta.bolsillo_id,
            tipo: 'ingreso',
            monto: total,
            descripcion: `Venta: ${producto.sku} ${producto.nombre} ×${fmtCant(cantidad)}`,
            fecha: formVenta.fecha,
          })
        : null;
      try {
        await q(supabase.from('ventas').insert({
          user_id: userId,
          producto_id: producto.id,
          fecha: formVenta.fecha,
          cantidad,
          precio_unitario: precio,
          costo_unitario: producto.costo_unitario, // se congela el costo de hoy para la ganancia de esta venta
          costo_extra: costoExtra,
          metodo_pago: formVenta.metodo_pago,
          documento,
          iva: ivaVenta,
          cliente: formVenta.cliente.trim() || null,
          nota: formVenta.nota.trim() || null,
          bolsillo_id: mov ? formVenta.bolsillo_id : null,
          movimiento_id: mov?.id || null,
        }));
      } catch (err) {
        await borrarMovimientoSimple(mov?.id);
        throw err;
      }
    }, loadData);
    if (ok) setVentaModal(false);
  };

  // Devolución o venta anulada: las unidades vuelven al stock y la venta deja de contar
  // como plata, pero el registro se conserva.
  const handleDevolverVenta = async (v) => {
    const devolver = !v.devuelta;
    const msg = devolver
      ? `¿Marcar como devuelta?\n\nLas ${fmtCant(v.cantidad)} unidades vuelven al stock y la venta deja de contar como plata.${v.movimiento_id ? '\n\nTambién se elimina el ingreso que se registró en la categoría.' : ''}`
      : '¿Deshacer la devolución?\n\nLas unidades vuelven a salir del stock.';
    if (!window.confirm(msg)) return;
    await run(async () => {
      if (devolver && v.movimiento_id) await borrarMovimientoSimple(v.movimiento_id);
      await q(supabase.from('ventas').update(devolver
        ? { devuelta: true, movimiento_id: null, bolsillo_id: null }
        : { devuelta: false }).eq('id', v.id));
    }, loadData);
  };

  const handleEliminarVenta = async (v) => {
    const msg = v.movimiento_id
      ? '¿Eliminar esta venta?\n\nLas unidades vuelven al stock y se elimina el ingreso que se registró en la categoría.'
      : '¿Eliminar esta venta?\n\nLas unidades vuelven al stock.';
    if (!window.confirm(msg)) return;
    await run(async () => {
      await q(supabase.from('ventas').delete().eq('id', v.id));
      await borrarMovimientoSimple(v.movimiento_id);
    }, loadData);
  };

  // --- Stock: entradas de mercadería y ajustes ---
  // Se abre desde un producto ("+ Stock" o "Ya compré") o desde cero con "+ Registrar compra",
  // eligiendo el producto —o creándolo— dentro del mismo formulario.
  const abrirStock = (producto = null, cantidadSugerida = '') => {
    setFormStock({
      ...stockVacio(),
      producto_id: producto?.id || '',
      cantidad: cantidadSugerida,
      costo_unitario: producto?.costo_unitario || '',
    });
    setStockModal(true);
  };

  const handleGuardarStock = async (e) => {
    e.preventDefault();
    const cantidad = num(formStock.cantidad);
    if (cantidad <= 0) return;
    if (!creandoProducto && !productoStock) { setError('Elige el producto o crea uno nuevo.'); return; }
    if (creandoProducto && (!formStock.nuevoSku.trim() || !formStock.nuevoNombre.trim())) {
      setError('El producto nuevo necesita SKU y nombre.');
      return;
    }
    const costoPagado = num(formStock.costo_unitario);
    // Con factura el IVA se recupera, así que el costo real del producto es el neto
    const costo = formStock.con_factura ? netoDesdeTotal(costoPagado) : costoPagado;

    const ok = await run(async () => {
      let producto = productoStock;

      // Producto nuevo: se crea con el costo de esta misma compra
      if (creandoProducto) {
        const sku = formStock.nuevoSku.trim().toUpperCase();
        try {
          producto = await q(supabase.from('productos').insert({
            user_id: userId,
            sku,
            nombre: formStock.nuevoNombre.trim(),
            categoria: formStock.nuevaCategoria.trim() || null,
            costo_unitario: costo,
            precio_venta: num(formStock.nuevoPrecio),
            stock_minimo: num(formStock.nuevoMinimo),
          }).select().single());
        } catch (err) {
          if (err.code === '23505') throw new Error(`Ya existe un producto con el SKU ${sku}.`);
          throw err;
        }
      }

      if (formStock.tipo === 'entrada') {
        const nuevoCosto = creandoProducto
          ? costo
          : costoPromedio(getStock(producto.id), producto.costo_unitario, cantidad, costo);
        await registrarEntrada(producto, {
          cantidad,
          costo,
          costoPagado,
          conFactura: formStock.con_factura,
          fecha: formStock.fecha,
          bolsilloId: formStock.bolsillo_id,
          proveedor: formStock.proveedor,
          nota: formStock.nota,
        });
        if (nuevoCosto !== Number(producto.costo_unitario)) {
          await q(supabase.from('productos').update({ costo_unitario: nuevoCosto }).eq('id', producto.id));
        }
      } else {
        await q(supabase.from('movimientos_stock').insert({
          user_id: userId,
          producto_id: producto.id,
          tipo: 'ajuste',
          cantidad: formStock.direccion === 'restar' ? -cantidad : cantidad,
          costo_unitario: producto.costo_unitario,
          fecha: formStock.fecha,
          nota: formStock.nota || null,
        }));
      }
    }, loadData);
    if (ok) setStockModal(null);
  };

  const handleEliminarMovStock = async (m) => {
    const msg = m.movimiento_id
      ? '¿Eliminar este movimiento de stock?\n\nTambién se elimina la salida de dinero asociada. El costo promedio del producto no cambia; si hace falta, corrígelo editando el producto.'
      : '¿Eliminar este movimiento de stock?';
    if (!window.confirm(msg)) return;
    await run(async () => {
      await q(supabase.from('movimientos_stock').delete().eq('id', m.id));
      await borrarMovimientoSimple(m.movimiento_id);
    }, loadData);
  };

  const handleAgregarLista = async (p, cantidad) => {
    const ok = await run(() => q(supabase.from('lista_compras').insert({
      user_id: userId,
      nombre: `${p.sku} · ${p.nombre}`,
      cantidad,
      unidad: 'unidad',
      precio_estimado: p.costo_unitario,
      comprado: false,
    })));
    if (ok) setAviso(`${p.sku} quedó en tu lista de compras (Mis Compras → Lista).`);
  };

  // --- Resumen del mes ---
  const ventasMes = ventas.filter(v => claveMes(v.fecha) === mesResumen && !v.devuelta);
  const ventasMesConMonto = ventasMes.filter(ventaConMonto);
  const vendidoMes = ventasMesConMonto.reduce((s, v) => s + totalVenta(v), 0);
  const gananciaMes = ventasMesConMonto.reduce((s, v) => s + gananciaVenta(v), 0);
  const unidadesMes = ventasMes.reduce((s, v) => s + Number(v.cantidad), 0);
  const porMetodo = METODOS.map(m => {
    const vs = ventasMes.filter(v => v.metodo_pago === m.value);
    return {
      ...m,
      total: vs.filter(ventaConMonto).reduce((s, v) => s + totalVenta(v), 0),
      n: vs.length,
      unidades: vs.reduce((s, v) => s + Number(v.cantidad), 0),
    };
  });
  const inventario = activos.reduce((acc, p) => {
    const s = Math.max(0, getStock(p.id));
    acc.unidades += s;
    acc.costo += s * Number(p.costo_unitario || 0);
    acc.venta += s * Number(p.precio_venta || 0);
    return acc;
  }, { unidades: 0, costo: 0, venta: 0 });
  const porProductoMes = ventasMes.reduce((acc, v) => { (acc[v.producto_id] = acc[v.producto_id] || []).push(v); return acc; }, {});
  const topMes = Object.entries(porProductoMes)
    .map(([id, vs]) => ({ producto: productoPorId(id), ...metricasProducto(vs) }))
    .sort((a, b) => b.ganancia - a.ganancia);
  const mesesDisponibles = [...new Set([claveMes(hoyISO()), ...ventas.map(v => claveMes(v.fecha))])]
    .sort((a, b) => b.localeCompare(a));

  // --- Qué comprar: stock en o bajo el mínimo, o menos de lo vendido en 30 días ---
  const reponer = activos.map(p => {
    const s = getStock(p.id);
    const v30 = vendidos30(p.id);
    const minimo = Number(p.stock_minimo || 0);
    const objetivo = Math.max(minimo, v30);
    const mejor = mejorProveedor(movStock, p.id);
    // La reposición se estima con el precio del proveedor más barato, no con el promedio
    const precio = mejor?.ultimoCosto || Number(p.costo_unitario || 0);
    return { p, stock: s, v30, minimo, mejor, precio, sugerido: Math.max(1, Math.ceil(objetivo - s)), listar: s <= minimo || s < v30 };
  }).filter(r => r.listar).sort((a, b) => (a.stock - a.minimo) - (b.stock - b.minimo));
  const costoReponer = reponer.reduce((s, r) => s + r.sugerido * r.precio, 0);

  // --- Lista de productos ---
  const texto = busqueda.trim().toLowerCase();
  const listaProductos = productos
    .filter(p => (verArchivados ? !p.activo : p.activo))
    .filter(p => !texto || p.sku.toLowerCase().includes(texto) || p.nombre.toLowerCase().includes(texto))
    .filter(p => !filtroCategoria || (filtroCategoria === '__sin'
      ? !(p.categoria || '').trim()
      : (p.categoria || '').trim() === filtroCategoria));

  // --- Ventas agrupadas por mes ---
  const gruposVentas = agruparPorMes(ventas);
  const mesAbierto = (clave, idx) => (mesesAbiertos ? mesesAbiertos.has(clave) : idx === 0);
  const toggleMes = (clave) => setMesesAbiertos(prev => {
    const next = new Set(prev || (gruposVentas.length ? [gruposVentas[0].clave] : []));
    if (next.has(clave)) next.delete(clave); else next.add(clave);
    return next;
  });

  // Categorías ya usadas, para agrupar los desplegables y sugerirlas al escribir
  const categorias = [...new Set(productos.map(p => (p.categoria || '').trim()).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b));

  const gruposProductos = (() => {
    const grupos = {};
    activos.forEach(p => {
      const c = (p.categoria || '').trim() || 'Sin categoría';
      (grupos[c] = grupos[c] || []).push(p);
    });
    return Object.keys(grupos).sort((a, b) => a.localeCompare(b)).map(c => ({ categoria: c, productos: grupos[c] }));
  })();

  const etiquetaProducto = (p) => `${p.sku} — ${p.nombre} (stock ${fmtCant(getStock(p.id))})`;

  // Con más de una categoría el desplegable se agrupa; con una sola no vale la pena
  const opcionesProducto = () => (gruposProductos.length > 1
    ? gruposProductos.map(g => (
        <optgroup key={g.categoria} label={g.categoria}>
          {g.productos.map(p => <option key={p.id} value={p.id}>{etiquetaProducto(p)}</option>)}
        </optgroup>
      ))
    : activos.map(p => <option key={p.id} value={p.id}>{etiquetaProducto(p)}</option>));

  // Por Mercado Libre la venta solo descuenta stock: el resto se lleva por el SII
  const ventaEsMeli = formVenta.metodo_pago === 'mercadolibre';

  // Producto elegido en el formulario de compra / ajuste de stock
  const productoStock = productoPorId(formStock.producto_id) || null;
  const creandoProducto = formStock.producto_id === 'nuevo';

  if (loading) return (
    <div className="bolsillos-loading"><div className="loading-spinner"></div><p>Cargando...</p></div>
  );

  const selectBolsillo = (value, onChange, textoVacio) => (
    <select value={value} onChange={e => onChange(e.target.value)}>
      <option value="">{textoVacio}</option>
      {bolsillos.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
    </select>
  );

  return (
    <div className="deudas-page">
      <div className="page-header">
        <div>
          <h2>Mi Negocio</h2>
          <p className="page-subtitle">Stock, ventas y ganancia real de lo que vendes</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn-secondary" onClick={() => abrirStock()}>+ Registrar compra</button>
          {activos.length > 0
            ? <button className="btn-primary" onClick={() => abrirVenta()}>+ Registrar venta</button>
            : <button className="btn-primary" onClick={abrirNuevoProducto}>+ Crear producto</button>}
        </div>
      </div>

      <ErrorBanner error={error} onClose={() => setError('')} />
      {aviso && (
        <div className="auth-success" style={{ marginBottom: 16 }}>
          {aviso}{' '}
          <button onClick={() => setAviso('')} style={{ background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer', color: 'inherit', padding: 0 }}>Cerrar</button>
        </div>
      )}

      {productos.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)', background: 'white', borderRadius: 12 }}>
          <div style={{ fontSize: '3rem', marginBottom: 16 }}>🏷️</div>
          <p style={{ fontWeight: 600, marginBottom: 8 }}>Aún no tienes productos</p>
          <p style={{ fontSize: '0.9rem' }}>Crea tus productos con su SKU, lo que te costaron y a cuánto los vendes. Después registra cada venta y verás tu ganancia real.</p>
          <button className="btn-primary" onClick={abrirNuevoProducto} style={{ marginTop: 16 }}>Crear mi primer producto</button>
        </div>
      ) : (
        <>
          <div className="negocio-tabs">
            {TABS.map(t => (
              <button key={t.key} className={`negocio-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
                {t.label}{t.key === 'reponer' && reponer.length > 0 ? ` (${reponer.length})` : ''}
              </button>
            ))}
          </div>

          {/* ─── RESUMEN ─── */}
          {tab === 'resumen' && (
            <>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Mes:</span>
                <select value={mesResumen} onChange={e => setMesResumen(e.target.value)} style={{ width: 'auto' }}>
                  {mesesDisponibles.map(c => <option key={c} value={c}>{nombreMes(c)}</option>)}
                </select>
              </div>

              <div className="summary-cards" style={{ marginBottom: 16 }}>
                <div className="card">
                  <div className="card-icon">💰</div>
                  <div className="card-content">
                    <h4>Vendido</h4>
                    <p className="card-amount neutral">{formatCLP(vendidoMes)}</p>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{ventasMes.length} ventas · {fmtCant(unidadesMes)} unidades</span>
                  </div>
                </div>
                <div className="card">
                  <div className="card-icon">📈</div>
                  <div className="card-content">
                    <h4>Ganancia real</h4>
                    <p className={`card-amount ${gananciaMes >= 0 ? 'positive' : 'negative'}`}>{formatCLP(gananciaMes)}</p>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      Margen {fmtPct(vendidoMes > 0 ? (gananciaMes / vendidoMes) * 100 : 0)} · ya descontado costo y extras
                    </span>
                  </div>
                </div>
                {porMetodo.map(m => (
                  <div key={m.value} className="card">
                    <div className="card-icon">{m.icono}</div>
                    <div className="card-content">
                      <h4>{m.titulo}</h4>
                      <p className="card-amount neutral">{formatCLP(m.total)}</p>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        {m.n} venta{m.n !== 1 ? 's' : ''} · {fmtCant(m.unidades)} unidades
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <h3 style={{ margin: '8px 0 12px', color: 'var(--text-secondary)', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                Tu inventario hoy
              </h3>
              <div className="summary-cards" style={{ marginBottom: 16 }}>
                <div className="card">
                  <div className="card-icon">📦</div>
                  <div className="card-content">
                    <h4>Unidades en stock</h4>
                    <p className="card-amount neutral">{fmtCant(inventario.unidades)}</p>
                  </div>
                </div>
                <div className="card">
                  <div className="card-icon">🪙</div>
                  <div className="card-content">
                    <h4>Invertido en stock</h4>
                    <p className="card-amount neutral">{formatCLP(inventario.costo)}</p>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Lo que te costó la mercadería que tienes</span>
                  </div>
                </div>
                <div className="card">
                  <div className="card-icon">🎯</div>
                  <div className="card-content">
                    <h4>Si vendes todo</h4>
                    <p className="card-amount positive">{formatCLP(inventario.venta)}</p>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Ganancia potencial {formatCLP(inventario.venta - inventario.costo)}</span>
                  </div>
                </div>
              </div>

              {reponer.length > 0 && (
                <div style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: '0.88rem', color: '#92400e', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ flex: 1 }}>⚠ {reponer.length} producto{reponer.length > 1 ? 's' : ''} con stock bajo.</span>
                  <button className="btn-secondary" style={{ fontSize: '0.82rem', padding: '4px 12px' }} onClick={() => setTab('reponer')}>Ver qué comprar</button>
                </div>
              )}

              <div className="card panel">
                <h3 style={{ fontSize: '1rem', marginTop: 0 }}>Ganancia por producto — {nombreMes(mesResumen)}</h3>
                {topMes.length === 0 ? (
                  <p className="empty-text">No hay ventas en este mes.</p>
                ) : (
                  <div className="tabla-scroll">
                    <table className="tabla-negocio">
                      <thead>
                        <tr>
                          <th>Producto</th>
                          <th className="num">Unidades</th>
                          <th className="num">Vendido</th>
                          <th className="num">Ganancia real</th>
                          <th className="num">Por unidad</th>
                          <th className="num">Margen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topMes.map(r => (
                          <tr key={r.producto?.id || 'x'}>
                            <td><strong>{r.producto?.sku}</strong> {r.producto?.nombre}</td>
                            <td className="num">{fmtCant(r.unidades)}</td>
                            <td className="num">{formatCLP(r.ingresos)}</td>
                            <td className={`num ${r.ganancia >= 0 ? 'text-success' : 'text-danger'}`}><strong>{formatCLP(r.ganancia)}</strong></td>
                            <td className="num">{formatCLP(r.gananciaPorUnidad)}</td>
                            <td className="num">{fmtPct(r.margen)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ─── PRODUCTOS ─── */}
          {tab === 'productos' && (
            <div className="card panel">
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
                <input type="text" value={busqueda} onChange={e => setBusqueda(e.target.value)}
                  placeholder="Buscar por SKU o nombre..." style={{ flex: '1 1 160px' }} />
                {categorias.length > 0 && (
                  <select value={filtroCategoria} onChange={e => setFiltroCategoria(e.target.value)} style={{ width: 'auto' }}>
                    <option value="">Todas las categorías</option>
                    {categorias.map(c => <option key={c} value={c}>{c}</option>)}
                    <option value="__sin">Sin categoría</option>
                  </select>
                )}
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={verArchivados} onChange={e => setVerArchivados(e.target.checked)} />
                  Ver archivados
                </label>
                <button className="btn-primary" onClick={abrirNuevoProducto}>+ Nuevo producto</button>
              </div>

              {listaProductos.length === 0 ? (
                <p className="empty-text">{verArchivados ? 'No hay productos archivados.' : 'No hay productos que coincidan.'}</p>
              ) : (
                <div className="tabla-scroll">
                  <table className="tabla-negocio">
                    <thead>
                      <tr>
                        <th>Producto</th>
                        <th className="num">Stock</th>
                        <th className="num">Costo</th>
                        <th className="num">Precio</th>
                        <th className="num">Gana por unidad</th>
                        <th className="num">Vendidas</th>
                        <th className="num">Ganancia total</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {listaProductos.map(p => {
                        const m = metricasProducto(ventasDe(p.id));
                        const margenUnidad = Number(p.precio_venta || 0) - Number(p.costo_unitario || 0);
                        const abierto = expandido === p.id;
                        const historial = movStock.filter(x => x.producto_id === p.id);
                        return (
                          <React.Fragment key={p.id}>
                            <tr>
                              <td>
                                <button onClick={() => setExpandido(abierto ? null : p.id)}
                                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', color: 'inherit', font: 'inherit' }}>
                                  <strong>{p.sku}</strong> {p.nombre} <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{abierto ? '▲' : '▼'}</span>
                                </button>
                                {(p.categoria || p.descripcion) && (
                                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                    {p.categoria && <span className="badge" style={{ marginRight: 6 }}>{p.categoria}</span>}
                                    {p.descripcion}
                                  </div>
                                )}
                              </td>
                              <td className="num">{badgeStock(p)}</td>
                              <td className="num">{formatCLP(p.costo_unitario)}</td>
                              <td className="num">{formatCLP(p.precio_venta)}</td>
                              <td className={`num ${margenUnidad >= 0 ? 'text-success' : 'text-danger'}`}>{formatCLP(margenUnidad)}</td>
                              <td className="num">{fmtCant(m.unidades)}</td>
                              <td className={`num ${m.ganancia >= 0 ? 'text-success' : 'text-danger'}`}>
                                <strong>{formatCLP(m.ganancia)}</strong>
                                {m.unidades > 0 && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 400 }}>real {formatCLP(m.gananciaPorUnidad)}/u</div>}
                              </td>
                              <td className="num">
                                <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                                  {p.activo && <button className="btn-secondary" style={{ padding: '4px 8px', fontSize: '0.78rem' }} onClick={() => abrirVenta(p)} title="Registrar venta">Vender</button>}
                                  {p.activo && <button className="btn-secondary" style={{ padding: '4px 8px', fontSize: '0.78rem' }} onClick={() => abrirStock(p)} title="Entrada o ajuste de stock">+ Stock</button>}
                                  <button className="btn-secondary" style={{ padding: '4px 8px', fontSize: '0.78rem' }} onClick={() => abrirEditarProducto(p)} title="Editar">✎</button>
                                </div>
                              </td>
                            </tr>
                            {abierto && (
                              <tr>
                                <td colSpan={8} style={{ background: 'var(--bg)' }}>
                                  <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                                    <button className="btn-secondary" style={{ padding: '4px 10px', fontSize: '0.78rem' }} onClick={() => handleArchivar(p)} disabled={saving}>
                                      {p.activo ? 'Archivar' : 'Reactivar'}
                                    </button>
                                    {ventasDe(p.id).length === 0 && (
                                      <button className="btn-secondary" style={{ padding: '4px 10px', fontSize: '0.78rem', color: 'var(--danger)' }} onClick={() => handleEliminarProducto(p)} disabled={saving}>
                                        × Eliminar
                                      </button>
                                    )}
                                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', alignSelf: 'center' }}>
                                      Stock mínimo: {fmtCant(p.stock_minimo)}{ventasDe(p.id).length > 0 ? ' · Tiene ventas: solo se puede archivar' : ''}
                                    </span>
                                  </div>
                                  {(() => {
                                    const proveedores = preciosPorProveedor(movStock, p.id);
                                    if (proveedores.length === 0) return null;
                                    return (
                                      <div style={{ marginBottom: 12 }}>
                                        <strong style={{ fontSize: '0.8rem' }}>Precios por proveedor</strong>
                                        <div style={{ display: 'grid', gap: 4, marginTop: 4, fontSize: '0.82rem' }}>
                                          {proveedores.map((pr, i) => (
                                            <div key={pr.proveedor} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                                              <span>
                                                {i === 0 && proveedores.length > 1 && (
                                                  <span className="badge badge-success" style={{ marginRight: 6 }}>más barato</span>
                                                )}
                                                {pr.proveedor}
                                                <span style={{ color: 'var(--text-muted)' }}>
                                                  {' '}· {pr.veces} compra{pr.veces !== 1 ? 's' : ''}
                                                  {pr.ultimaFecha ? ` · última ${formatFecha(pr.ultimaFecha)}` : ''}
                                                </span>
                                              </span>
                                              <span>
                                                <strong>{formatCLP(pr.ultimoCosto)}</strong>
                                                {pr.costoPromedio !== pr.ultimoCosto && (
                                                  <span style={{ color: 'var(--text-muted)' }}> · promedio {formatCLP(pr.costoPromedio)}</span>
                                                )}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                        <span className="field-hint">
                                          Se compara el costo real: en las compras con factura el IVA ya va descontado.
                                        </span>
                                      </div>
                                    );
                                  })()}
                                  <strong style={{ fontSize: '0.8rem' }}>Entradas y ajustes de stock</strong>
                                  {historial.length === 0 ? (
                                    <p className="empty-text" style={{ margin: '4px 0' }}>Sin movimientos de stock.</p>
                                  ) : historial.map(x => (
                                    <div key={x.id} className="abono-item">
                                      <span>
                                        {formatFecha(x.fecha)} — {x.tipo === 'entrada' ? '📦 Entrada' : '✏️ Ajuste'} {x.cantidad > 0 ? '+' : ''}{fmtCant(x.cantidad)}
                                        {x.tipo === 'entrada' && ` a ${formatCLP(x.costo_unitario)} c/u`}
                                        {x.proveedor && ` · ${x.proveedor}`}
                                        {x.nota && ` · ${x.nota}`}
                                        {x.bolsillo_id && ` · pagado desde ${nombreBolsillo(x.bolsillo_id) || 'categoría'}`}
                                      </span>
                                      <button className="btn-icon-del small" onClick={() => handleEliminarMovStock(x)}>×</button>
                                    </div>
                                  ))}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ─── VENTAS ─── */}
          {tab === 'ventas' && (
            ventas.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-muted)', background: 'white', borderRadius: 12 }}>
                <div style={{ fontSize: '3rem', marginBottom: 16 }}>🧾</div>
                <p>Aún no registras ventas.</p>
                <button className="btn-primary" onClick={() => abrirVenta()} style={{ marginTop: 12 }}>+ Registrar venta</button>
              </div>
            ) : (
              <div className="meses-list">
                {gruposVentas.map((g, idx) => {
                  const vendido = g.registros.reduce((s, v) => s + totalVenta(v), 0);
                  const ganancia = g.registros.reduce((s, v) => s + gananciaVenta(v), 0);
                  const abierto = mesAbierto(g.clave, idx);
                  return (
                    <div key={g.clave} className="mes-grupo">
                      <button type="button" className="mes-header" onClick={() => toggleMes(g.clave)}>
                        <span className="mes-chevron">{abierto ? '▼' : '▶'}</span>
                        <span className="mes-nombre">
                          {g.nombre}
                          <span className="mes-count">{g.registros.length} venta{g.registros.length !== 1 ? 's' : ''}</span>
                        </span>
                        <span className="mes-totales">
                          <span>Vendido {formatCLP(vendido)}</span>
                          <strong className={ganancia >= 0 ? 'text-success' : 'text-danger'}>Ganancia {formatCLP(ganancia)}</strong>
                        </span>
                      </button>
                      {abierto && (
                        <div className="movimientos-list mes-body">
                          {g.registros.map(v => {
                            const p = productoPorId(v.producto_id);
                            const gan = gananciaVenta(v);
                            const sinMonto = Number(v.precio_unitario) <= 0;
                            return (
                              <div key={v.id} className="movimiento-item" style={v.devuelta ? { opacity: 0.55 } : undefined}>
                                <div className="mov-info">
                                  <span className="mov-tipo-badge ingreso">{v.devuelta ? '↩' : iconoMetodo(v.metodo_pago)}</span>
                                  <div>
                                    <span className="mov-desc" style={v.devuelta ? { textDecoration: 'line-through' } : undefined}>
                                      <strong>{p?.sku}</strong> {p?.nombre} ×{fmtCant(v.cantidad)}
                                      {v.cliente && <span style={{ color: 'var(--text-muted)' }}> · {v.cliente}</span>}
                                    </span>
                                    <span className="mov-fecha">
                                      {formatFecha(v.fecha)}
                                      {v.devuelta && ' · devuelta'}
                                      {sinMonto ? ' · solo stock' : ` · ${formatCLP(v.precio_unitario)} c/u`}
                                      {Number(v.costo_extra) > 0 && ` · extras ${formatCLP(v.costo_extra)}`}
                                      {v.bolsillo_id && ` · → ${nombreBolsillo(v.bolsillo_id) || 'categoría'}`}
                                    </span>
                                  </div>
                                </div>
                                <div className="mov-right">
                                  <span style={{ textAlign: 'right' }}>
                                    <span className="mov-monto">{sinMonto || v.devuelta ? '—' : formatCLP(totalVenta(v))}</span>
                                    {!sinMonto && !v.devuelta && (
                                      <span style={{ display: 'block', fontSize: '0.75rem' }} className={gan >= 0 ? 'text-success' : 'text-danger'}>
                                        gana {formatCLP(gan)}
                                      </span>
                                    )}
                                  </span>
                                  <button className="btn-icon-del small" title={v.devuelta ? 'Deshacer la devolución' : 'Marcar como devuelta'}
                                    onClick={() => handleDevolverVenta(v)} style={{ color: 'var(--primary)', fontSize: '0.9rem' }}>↩</button>
                                  <button className="btn-icon-del small" title="Eliminar" onClick={() => handleEliminarVenta(v)}>×</button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )
          )}

          {/* ─── QUÉ COMPRAR ─── */}
          {/* ─── IVA ─── */}
          {tab === 'iva' && (() => {
            const r = ivaDelMes({ clave: mesResumen, ventas, compras, movStock, movimientos: egresos });
            const aPagar = r.saldo >= 0;
            const conMovimiento = r.ventasMes.length + r.mercaderiaMes.length + r.comprasMes.length + r.gastosMes.length;
            return (
              <>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Mes:</span>
                  <select value={mesResumen} onChange={e => setMesResumen(e.target.value)} style={{ width: 'auto' }}>
                    {mesesDisponibles.map(c => <option key={c} value={c}>{nombreMes(c)}</option>)}
                  </select>
                </div>

                <div className="summary-cards" style={{ marginBottom: 16 }}>
                  <div className="card">
                    <div className="card-icon">🧾</div>
                    <div className="card-content">
                      <h4>IVA de tus ventas</h4>
                      <p className="card-amount neutral">{formatCLP(r.debito)}</p>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>débito · {r.ventasMes.length} venta{r.ventasMes.length !== 1 ? 's' : ''} con documento</span>
                    </div>
                  </div>
                  <div className="card">
                    <div className="card-icon">📄</div>
                    <div className="card-content">
                      <h4>IVA de tus compras</h4>
                      <p className="card-amount positive">{formatCLP(r.credito)}</p>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>crédito · solo lo que tiene factura</span>
                    </div>
                  </div>
                  <div className="card">
                    <div className="card-icon">{aPagar ? '💸' : '🎁'}</div>
                    <div className="card-content">
                      <h4>{aPagar ? 'Te toca pagar' : 'Queda a tu favor'}</h4>
                      <p className={`card-amount ${aPagar ? 'negative' : 'positive'}`}>{formatCLP(Math.abs(r.saldo))}</p>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>referencia para el F29</span>
                    </div>
                  </div>
                </div>

                <div className="card panel" style={{ marginBottom: 16 }}>
                  <h3 style={{ fontSize: '1rem', marginTop: 0 }}>De dónde sale el IVA crédito</h3>
                  <div style={{ display: 'grid', gap: 6, fontSize: '0.88rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <span>Mercadería que compraste ({r.mercaderiaMes.length})</span><strong>{formatCLP(r.creditoMercaderia)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <span>Compras de Mis Compras ({r.comprasMes.length})</span><strong>{formatCLP(r.creditoCompras)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <span>Otros gastos con factura ({r.gastosMes.length})</span><strong>{formatCLP(r.creditoGastos)}</strong>
                    </div>
                  </div>
                  <p className="field-hint" style={{ marginBottom: 0 }}>
                    Para que algo sume acá, marca "con factura" al registrarlo: al comprar mercadería, en Mis Compras o en una salida de dinero de Mis Categorías. Es una referencia para armar el F29, no reemplaza la declaración.
                  </p>
                </div>

                <div className="card panel">
                  <h3 style={{ fontSize: '1rem', marginTop: 0 }}>Detalle del mes</h3>
                  {conMovimiento === 0 ? (
                    <p className="empty-text">Nada con IVA en este mes.</p>
                  ) : (
                    <div className="tabla-scroll">
                      <table className="tabla-negocio">
                        <thead>
                          <tr>
                            <th>Fecha</th>
                            <th>Detalle</th>
                            <th>Documento</th>
                            <th className="num">Total</th>
                            <th className="num">IVA</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.ventasMes.map(v => {
                            const p = productoPorId(v.producto_id);
                            return (
                              <tr key={'v' + v.id}>
                                <td>{formatFecha(v.fecha)}</td>
                                <td>Venta {p?.sku || ''} ×{fmtCant(v.cantidad)}</td>
                                <td>{nombreDocumento(v.documento)}</td>
                                <td className="num">{formatCLP(totalVenta(v))}</td>
                                <td className="num text-danger">+{formatCLP(v.iva)}</td>
                              </tr>
                            );
                          })}
                          {r.mercaderiaMes.map(m => {
                            const p = productoPorId(m.producto_id);
                            return (
                              <tr key={'m' + m.id}>
                                <td>{formatFecha(m.fecha)}</td>
                                <td>Mercadería {p?.sku || ''} ×{fmtCant(m.cantidad)}</td>
                                <td>📄 Factura</td>
                                <td className="num">{formatCLP(num(m.cantidad) * num(m.costo_unitario) + num(m.iva))}</td>
                                <td className="num text-success">−{formatCLP(m.iva)}</td>
                              </tr>
                            );
                          })}
                          {r.comprasMes.map(c => (
                            <tr key={'c' + c.id}>
                              <td>{formatFecha(c.fecha)}</td>
                              <td>Compra {c.tienda || 'sin nombre'}</td>
                              <td>📄 Factura</td>
                              <td className="num">{formatCLP(c.total)}</td>
                              <td className="num text-success">−{formatCLP(c.iva)}</td>
                            </tr>
                          ))}
                          {r.gastosMes.map(m => (
                            <tr key={'g' + m.id}>
                              <td>{formatFecha(m.fecha)}</td>
                              <td>{m.descripcion || 'Gasto'}</td>
                              <td>📄 Factura</td>
                              <td className="num">{formatCLP(m.monto)}</td>
                              <td className="num text-success">−{formatCLP(m.iva)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            );
          })()}

          {tab === 'reponer' && (
            <div className="card panel">
              <h3 style={{ fontSize: '1rem', marginTop: 0, marginBottom: 4 }}>Qué tienes que comprar</h3>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '0 0 12px' }}>
                Productos en o bajo su stock mínimo, o con menos stock del que vendiste en los últimos 30 días.
                La cantidad sugerida cubre lo vendido en 30 días (o tu stock mínimo, lo que sea mayor).
              </p>
              {reponer.length === 0 ? (
                <p className="empty-text">¡Todo con stock suficiente! 🎉</p>
              ) : (
                <>
                  <div className="tabla-scroll">
                    <table className="tabla-negocio">
                      <thead>
                        <tr>
                          <th>Producto</th>
                          <th className="num">Stock</th>
                          <th className="num">Mínimo</th>
                          <th className="num">Vendidas 30 días</th>
                          <th className="num">Comprar</th>
                          <th>Mejor precio</th>
                          <th className="num">Costo estimado</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {reponer.map(r => (
                          <tr key={r.p.id}>
                            <td><strong>{r.p.sku}</strong> {r.p.nombre}</td>
                            <td className="num">{badgeStock(r.p)}</td>
                            <td className="num">{fmtCant(r.minimo)}</td>
                            <td className="num">{fmtCant(r.v30)}</td>
                            <td className="num"><strong>{fmtCant(r.sugerido)}</strong></td>
                            <td>
                              {r.mejor ? (
                                <>
                                  <strong>{formatCLP(r.mejor.ultimoCosto)}</strong>
                                  <span style={{ display: 'block', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                                    {r.mejor.proveedor}{r.mejor.conFactura ? ' · con factura' : ''}
                                  </span>
                                </>
                              ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                            </td>
                            <td className="num">{formatCLP(r.sugerido * r.precio)}</td>
                            <td className="num">
                              <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                                <button className="btn-secondary" style={{ padding: '4px 8px', fontSize: '0.78rem' }}
                                  onClick={() => handleAgregarLista(r.p, r.sugerido)} disabled={saving} title="Agregar a la lista de compras">📋 A la lista</button>
                                <button className="btn-primary" style={{ padding: '4px 8px', fontSize: '0.78rem' }}
                                  onClick={() => abrirStock(r.p, r.sugerido)}>Ya compré</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: '2px solid var(--border)', display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                    <span>Total estimado de reposición</span>
                    <span>{formatCLP(costoReponer)}</span>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* Modal: Producto */}
      {productoModal && (
        <div className="modal-overlay" onClick={() => setProductoModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{productoModal.id ? 'Editar producto' : 'Nuevo producto'}</h3>
              <button className="modal-close" onClick={() => setProductoModal(null)}>×</button>
            </div>
            <form onSubmit={handleGuardarProducto}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
                <div className="form-group">
                  <label>SKU (código)</label>
                  <input type="text" value={formProducto.sku} required autoFocus
                    onChange={e => setFormProducto({ ...formProducto, sku: e.target.value })}
                    placeholder="Ej: CAD-ORO-45" style={{ textTransform: 'uppercase' }} />
                </div>
                <div className="form-group">
                  <label>Nombre</label>
                  <input type="text" value={formProducto.nombre} required
                    onChange={e => setFormProducto({ ...formProducto, nombre: e.target.value })}
                    placeholder="Ej: Cadena oro 45 cm" />
                </div>
              </div>
              <div className="form-group">
                <label>Categoría</label>
                <input type="text" list="categorias-productos" value={formProducto.categoria}
                  onChange={e => setFormProducto({ ...formProducto, categoria: e.target.value })}
                  placeholder="Ej: Rosarios, Pulseras, Cadenas doradas" />
                <span className="field-hint">Agrupa los listados cuando tengas muchos productos.</span>
              </div>
              <datalist id="categorias-productos">
                {categorias.map(c => <option key={c} value={c} />)}
              </datalist>
              <div className="form-group">
                <label>Descripción (opcional)</label>
                <input type="text" value={formProducto.descripcion}
                  onChange={e => setFormProducto({ ...formProducto, descripcion: e.target.value })}
                  placeholder="Material, medida, color, proveedor..." />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
                <div className="form-group">
                  <label>¿Cuánto te costó? (c/u)</label>
                  <input type="number" value={formProducto.costo_unitario} min="0"
                    onChange={e => setFormProducto({ ...formProducto, costo_unitario: e.target.value })} placeholder="0" />
                </div>
                <div className="form-group">
                  <label>Precio de venta (c/u)</label>
                  <input type="number" value={formProducto.precio_venta} min="0"
                    onChange={e => setFormProducto({ ...formProducto, precio_venta: e.target.value })} placeholder="0" />
                </div>
                <div className="form-group">
                  <label>Stock mínimo</label>
                  <input type="number" value={formProducto.stock_minimo} min="0"
                    onChange={e => setFormProducto({ ...formProducto, stock_minimo: e.target.value })} placeholder="Ej: 3" />
                </div>
              </div>
              {(() => {
                const costo = num(formProducto.costo_unitario);
                const precio = num(formProducto.precio_venta);
                if (!precio) return null;
                const gana = precio - costo;
                return (
                  <div className="ganancia-preview">
                    <div><span>Ganas por unidad</span><strong className={gana >= 0 ? 'text-success' : 'text-danger'}>{formatCLP(gana)}</strong></div>
                    <div><span>Margen</span><strong>{fmtPct((gana / precio) * 100)}</strong></div>
                  </div>
                );
              })()}
              {!productoModal.id && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
                  <div className="form-group">
                    <label>¿Cuántas tienes hoy?</label>
                    <input type="number" value={formProducto.stock_inicial} min="0"
                      onChange={e => setFormProducto({ ...formProducto, stock_inicial: e.target.value })} placeholder="0" />
                  </div>
                  {num(formProducto.stock_inicial) > 0 && (
                    <div className="form-group">
                      <label>¿Lo pagaste de una categoría?</label>
                      {selectBolsillo(formProducto.bolsillo_id, v => setFormProducto({ ...formProducto, bolsillo_id: v }), 'No, ya estaba pagado')}
                      {formProducto.bolsillo_id && (
                        <span className="field-hint">Se registrará una salida de {formatCLP(num(formProducto.stock_inicial) * num(formProducto.costo_unitario))}</span>
                      )}
                    </div>
                  )}
                </div>
              )}
              {productoModal.id && (
                <p className="field-hint" style={{ marginTop: 0 }}>
                  El costo se actualiza solo con cada compra de mercadería (promedio). Cambiarlo aquí no altera las ventas ya registradas.
                </p>
              )}
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setProductoModal(null)}>Cancelar</button>
                <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Guardando...' : 'Guardar producto'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Venta */}
      {ventaModal && (
        <div className="modal-overlay" onClick={() => setVentaModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🧾 Registrar venta</h3>
              <button className="modal-close" onClick={() => setVentaModal(false)}>×</button>
            </div>
            <form onSubmit={handleGuardarVenta}>
              <div className="form-group">
                <label>Producto</label>
                <select value={formVenta.producto_id} required
                  onChange={e => {
                    const p = productoPorId(e.target.value);
                    setFormVenta({ ...formVenta, producto_id: e.target.value, precio_unitario: p?.precio_venta || '' });
                  }}>
                  <option value="">Selecciona...</option>
                  {opcionesProducto()}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
                <div className="form-group">
                  <label>Cantidad</label>
                  <input type="number" value={formVenta.cantidad} min="0.01" step="any" required
                    onChange={e => setFormVenta({ ...formVenta, cantidad: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>{ventaEsMeli ? 'Precio (c/u) — opcional' : 'Precio (c/u) con IVA'}</label>
                  <input type="number" value={formVenta.precio_unitario} min="0" required={!ventaEsMeli}
                    onChange={e => setFormVenta({ ...formVenta, precio_unitario: e.target.value })} />
                  {ventaEsMeli && <span className="field-hint">Puedes dejarlo vacío: la venta solo descuenta stock.</span>}
                </div>
                {!ventaEsMeli && (
                  <div className="form-group">
                    <label>Gastos extra</label>
                    <input type="number" value={formVenta.costo_extra} min="0" placeholder="0"
                      onChange={e => setFormVenta({ ...formVenta, costo_extra: e.target.value })} />
                    <span className="field-hint">Envío, comisión, bolsa…</span>
                  </div>
                )}
              </div>
              <div className="form-group" hidden={ventaEsMeli}>
                <label>¿Con qué documento?</label>
                <div className="tipo-toggle" style={{ flexWrap: 'wrap' }}>
                  {DOCUMENTOS_VENTA.map(d => (
                    <button key={d.value} type="button"
                      className={`toggle-btn ${formVenta.documento === d.value ? 'active-green' : ''}`}
                      onClick={() => setFormVenta({ ...formVenta, documento: d.value })}>
                      {d.label}
                    </button>
                  ))}
                </div>
                <span className="field-hint">
                  {conIva(formVenta.documento)
                    ? 'El precio incluye IVA: se separa solo y suma al IVA que tienes que declarar.'
                    : 'Sin documento no se declara IVA por esta venta.'}
                </span>
              </div>
              <div className="form-group">
                <label>¿Cómo te pagaron?</label>
                <div className="tipo-toggle">
                  {METODOS.map(m => (
                    <button key={m.value} type="button"
                      className={`toggle-btn ${formVenta.metodo_pago === m.value ? 'active-green' : ''}`}
                      onClick={() => setFormVenta({ ...formVenta, metodo_pago: m.value, bolsillo_id: bolsilloPorMetodo(m.value) })}>
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
              {ventaEsMeli ? (
                <p className="field-hint" style={{ marginTop: 0 }}>
                  Por Mercado Libre solo se descuenta el stock: nada de documento, gastos ni categoría.
                  Esa venta ya queda registrada en el SII por la plataforma, y el depósito de Meli lo
                  anotas aparte como entrada de dinero cuando llegue.
                </p>
              ) : (
                <div className="form-group">
                  <label>¿A qué categoría entra el dinero?</label>
                  {selectBolsillo(formVenta.bolsillo_id, v => setFormVenta({ ...formVenta, bolsillo_id: v }), 'No registrar en ninguna categoría')}
                  {formVenta.bolsillo_id && <span className="field-hint">Se registrará el ingreso automáticamente</span>}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
                <div className="form-group">
                  <label>Fecha</label>
                  <input type="date" value={formVenta.fecha} onChange={e => setFormVenta({ ...formVenta, fecha: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Cliente (opcional)</label>
                  <input type="text" value={formVenta.cliente} onChange={e => setFormVenta({ ...formVenta, cliente: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label>Nota (opcional)</label>
                <input type="text" value={formVenta.nota} onChange={e => setFormVenta({ ...formVenta, nota: e.target.value })} />
              </div>
              {(() => {
                const p = productoPorId(formVenta.producto_id);
                if (!p) return null;
                const cant = num(formVenta.cantidad);
                const total = cant * num(formVenta.precio_unitario);
                const ivaVenta = conIva(formVenta.documento) ? ivaDesdeTotal(total) : 0;
                const neto = total - ivaVenta;
                const costoMerc = cant * Number(p.costo_unitario || 0);
                const extra = num(formVenta.costo_extra);
                const ganancia = neto - costoMerc - extra;
                return (
                  <div className="ganancia-preview">
                    <div><span>Te pagan</span><strong>{formatCLP(total)}</strong></div>
                    {ivaVenta > 0 && <div><span>IVA que debes declarar</span><span>−{formatCLP(ivaVenta)}</span></div>}
                    <div><span>Costo de la mercadería</span><span>−{formatCLP(costoMerc)}</span></div>
                    {extra > 0 && <div><span>Gastos extra</span><span>−{formatCLP(extra)}</span></div>}
                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 4 }}>
                      <span>Ganancia real</span>
                      <strong className={ganancia >= 0 ? 'text-success' : 'text-danger'}>
                        {formatCLP(ganancia)} {neto > 0 && `(${fmtPct((ganancia / neto) * 100)})`}
                      </strong>
                    </div>
                  </div>
                );
              })()}
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setVentaModal(false)}>Cancelar</button>
                <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Guardando...' : 'Registrar venta'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Stock */}
      {stockModal && (
        <div className="modal-overlay" onClick={() => setStockModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📦 {productoStock ? `${productoStock.sku} — ${productoStock.nombre}` : 'Registrar compra de mercadería'}</h3>
              <button className="modal-close" onClick={() => setStockModal(null)}>×</button>
            </div>
            <div className="form-group">
              <label>Producto</label>
              <select value={formStock.producto_id}
                onChange={e => {
                  const elegido = productoPorId(e.target.value);
                  setFormStock({
                    ...formStock,
                    producto_id: e.target.value,
                    costo_unitario: elegido?.costo_unitario || '',
                    tipo: e.target.value === 'nuevo' ? 'entrada' : formStock.tipo,
                  });
                }}>
                <option value="">Selecciona...</option>
                {opcionesProducto()}
                <option value="nuevo">➕ Crear un producto nuevo</option>
              </select>
            </div>
            {creandoProducto && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
                <div className="form-group">
                  <label>SKU</label>
                  <input type="text" value={formStock.nuevoSku} required
                    onChange={e => setFormStock({ ...formStock, nuevoSku: e.target.value })}
                    placeholder="Ej: CAD-ORO-45" style={{ textTransform: 'uppercase' }} />
                </div>
                <div className="form-group">
                  <label>Nombre</label>
                  <input type="text" value={formStock.nuevoNombre} required
                    onChange={e => setFormStock({ ...formStock, nuevoNombre: e.target.value })}
                    placeholder="Ej: Cadena oro 45 cm" />
                </div>
                <div className="form-group">
                  <label>Categoría</label>
                  <input type="text" list="categorias-stock" value={formStock.nuevaCategoria}
                    onChange={e => setFormStock({ ...formStock, nuevaCategoria: e.target.value })}
                    placeholder="Ej: Rosarios, Pulseras" />
                  <datalist id="categorias-stock">
                    {categorias.map(c => <option key={c} value={c} />)}
                  </datalist>
                </div>
                <div className="form-group">
                  <label>Precio de venta</label>
                  <input type="number" value={formStock.nuevoPrecio} min="0"
                    onChange={e => setFormStock({ ...formStock, nuevoPrecio: e.target.value })} placeholder="0" />
                </div>
                <div className="form-group">
                  <label>Stock mínimo</label>
                  <input type="number" value={formStock.nuevoMinimo} min="0"
                    onChange={e => setFormStock({ ...formStock, nuevoMinimo: e.target.value })} placeholder="Ej: 3" />
                </div>
              </div>
            )}
            {productoStock && (
              <p style={{ margin: '0 0 12px', fontSize: '0.88rem', color: 'var(--text-secondary)' }}>
                Stock actual: <strong>{fmtCant(getStock(productoStock.id))}</strong> · Costo promedio: <strong>{formatCLP(productoStock.costo_unitario)}</strong>
              </p>
            )}
            <form onSubmit={handleGuardarStock}>
              <div className="form-group" hidden={creandoProducto}>
                <div className="tipo-toggle">
                  <button type="button" className={`toggle-btn ${formStock.tipo === 'entrada' ? 'active-green' : ''}`}
                    onClick={() => setFormStock({ ...formStock, tipo: 'entrada' })}>📦 Compré mercadería</button>
                  <button type="button" className={`toggle-btn ${formStock.tipo === 'ajuste' ? 'active-red' : ''}`}
                    onClick={() => setFormStock({ ...formStock, tipo: 'ajuste' })}>✏️ Ajuste</button>
                </div>
                {formStock.tipo === 'ajuste' && (
                  <span className="field-hint">Para pérdidas, regalos, productos dañados o corregir el conteo. No mueve dinero.</span>
                )}
              </div>
              {formStock.tipo === 'ajuste' && (
                <div className="form-group">
                  <div className="tipo-toggle">
                    <button type="button" className={`toggle-btn ${formStock.direccion === 'restar' ? 'active-red' : ''}`}
                      onClick={() => setFormStock({ ...formStock, direccion: 'restar' })}>− Restar</button>
                    <button type="button" className={`toggle-btn ${formStock.direccion === 'sumar' ? 'active-green' : ''}`}
                      onClick={() => setFormStock({ ...formStock, direccion: 'sumar' })}>+ Sumar</button>
                  </div>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
                <div className="form-group">
                  <label>Cantidad</label>
                  <input type="number" value={formStock.cantidad} min="0.01" step="any" required autoFocus
                    onChange={e => setFormStock({ ...formStock, cantidad: e.target.value })} />
                </div>
                {formStock.tipo === 'entrada' && (
                  <div className="form-group">
                    <label>Costo (c/u) pagado</label>
                    <input type="number" value={formStock.costo_unitario} min="0" required
                      onChange={e => setFormStock({ ...formStock, costo_unitario: e.target.value })} />
                  </div>
                )}
                <div className="form-group">
                  <label>Fecha</label>
                  <input type="date" value={formStock.fecha} onChange={e => setFormStock({ ...formStock, fecha: e.target.value })} />
                </div>
              </div>
              {formStock.tipo === 'entrada' && (
                <>
                  <div className="form-group">
                    <label>¿Te dieron factura?</label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 400 }}>
                      <input type="checkbox" checked={formStock.con_factura}
                        onChange={e => setFormStock({ ...formStock, con_factura: e.target.checked })}
                        style={{ width: 18, height: 18, accentColor: 'var(--primary)' }} />
                      Sí, con factura — el IVA se recupera
                    </label>
                    {formStock.con_factura && num(formStock.costo_unitario) > 0 && (
                      <span className="field-hint">
                        Costo real (neto) {formatCLP(netoDesdeTotal(num(formStock.costo_unitario)))} c/u · IVA de la compra {formatCLP(ivaDesdeTotal(num(formStock.cantidad) * num(formStock.costo_unitario)))}
                      </span>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Proveedor (opcional)</label>
                    <input type="text" value={formStock.proveedor} onChange={e => setFormStock({ ...formStock, proveedor: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>¿De qué categoría pagaste?</label>
                    {selectBolsillo(formStock.bolsillo_id, v => setFormStock({ ...formStock, bolsillo_id: v }), 'No descontar')}
                    {formStock.bolsillo_id && (
                      <span className="field-hint">Se registrará una salida de {formatCLP(num(formStock.cantidad) * num(formStock.costo_unitario))}</span>
                    )}
                  </div>
                  {productoStock && num(formStock.cantidad) > 0 && (
                    <p className="field-hint" style={{ marginTop: 0 }}>
                      Nuevo costo promedio: <strong>{formatCLP(costoPromedio(getStock(productoStock.id), productoStock.costo_unitario, num(formStock.cantidad), formStock.con_factura ? netoDesdeTotal(num(formStock.costo_unitario)) : num(formStock.costo_unitario)))}</strong>
                      {(() => {
                        const costoReal = formStock.con_factura ? netoDesdeTotal(num(formStock.costo_unitario)) : num(formStock.costo_unitario);
                        const promedioActual = Number(productoStock.costo_unitario || 0);
                        if (!costoReal || !promedioActual) return null;
                        const dif = Math.round(((costoReal - promedioActual) / promedioActual) * 100);
                        if (Math.abs(dif) < 3) return null;
                        return (
                          <span style={{ display: 'block', marginTop: 2, color: dif > 0 ? 'var(--danger)' : 'var(--success)' }}>
                            {dif > 0
                              ? `⚠ Te está saliendo ${dif}% más caro que tu costo promedio (${formatCLP(promedioActual)})`
                              : `✅ ${Math.abs(dif)}% más barato que tu costo promedio (${formatCLP(promedioActual)})`}
                          </span>
                        );
                      })()}
                    </p>
                  )}
                </>
              )}
              <div className="form-group">
                <label>Nota (opcional)</label>
                <input type="text" value={formStock.nota} onChange={e => setFormStock({ ...formStock, nota: e.target.value })}
                  placeholder={formStock.tipo === 'ajuste' ? 'Ej: se rompió, regalo, conteo' : ''} />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setStockModal(null)}>Cancelar</button>
                <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Negocio;
